/**
 * 카드 컴포넌트
 *
 * 디자인 시스템의 card-* 클래스를 활용한 컨테이너 컴포넌트입니다.
 * AppCard, AppCardHeader, AppCardContent, AppCardFooter를 조합하여 복잡한 레이아웃을 구성할 수 있습니다.
 *
 * @component
 * @example
 * // 기본 카드
 * <AppCard>
 *   <AppCardHeader>제목</AppCardHeader>
 *   <AppCardContent>내용</AppCardContent>
 * </AppCard>
 *
 * @example
 * // 풀 레이아웃
 * <AppCard>
 *   <AppCardHeader>플레이리스트</AppCardHeader>
 *   <AppCardContent>
 *     <p>플레이리스트 정보</p>
 *   </AppCardContent>
 *   <AppCardFooter>
 *     <AppButton>저장</AppButton>
 *   </AppCardFooter>
 * </AppCard>
 *
 * @typedef {object} AppCardProps
 * @property {boolean} [hover=true] - 호버 효과 활성화 여부
 * @property {string} [className] - 추가 CSS 클래스
 * @property {React.ReactNode} children - 카드 내용
 *
 * @returns {JSX.Element} 스타일이 적용된 카드 컨테이너
 */

import { HTMLAttributes, forwardRef } from 'react';
import { cn } from '@/lib/utils';

export interface AppCardProps extends HTMLAttributes<HTMLDivElement> {
  hover?: boolean;
}

export const AppCard = forwardRef<HTMLDivElement, AppCardProps>(
  ({ hover = true, className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('card', !hover && 'hover:shadow-md', className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);

AppCard.displayName = 'AppCard';

export const AppCardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('card-header', className)} {...props} />
  )
);

AppCardHeader.displayName = 'AppCardHeader';

export const AppCardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('card-content', className)} {...props} />
  )
);

AppCardContent.displayName = 'AppCardContent';

export const AppCardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('card-footer', className)} {...props} />
  )
);

AppCardFooter.displayName = 'AppCardFooter';
